import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";
import RateUpdateButton from "../../components/insurance/RateUpdateButton"; 
import InsuranceRateTable from "../../components/insurance/InsuranceRatesTable"; 
import API from "../../api/axiosConfig";

const InsuranceRatesDashboard = () => {
  const [rateData, setRateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch rates for the selected effective year
  const fetchRateData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await API.post(
        `/api/insurance/rates/table`,
      );

      setRateData(response.data);
    } catch (err) {
      console.error("Failed to load rate directory:", err);
      setError(
        err.response?.data?.message ||
          "Failed to load insurance rates. Please try again or upload a rate sheet.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRateData();
  }, [fetchRateData]);

  // Callback triggered when a new CSV is uploaded via RateUpdateButton
  const handleUploadSuccess = () => {
    fetchRateData();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>

      {/* Header Row: Title and Upload Button */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              Insurance Rate Directory
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              View active premium rates for Health, Dental, and Vision coverage
              tiers.
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            

            {/* Upload Modal Trigger Button */}
            <RateUpdateButton onSuccess={handleUploadSuccess} />
          </Stack>
        </Stack>
      </Paper>

      {/* Error Banner */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Rate Content */}
      {loading ? (
        <Paper
          sx={{
            p: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
          }}
        >
          <CircularProgress size={40} />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Loading rate directory...
          </Typography>
        </Paper>
      ) : (
        <InsuranceRateTable rateData={rateData} />
      )}
    </Container>
  );
};

export default InsuranceRatesDashboard;
